import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import {
  getAllPossibleSecondaryDomains,
  getSecondaryDomains,
  UserKeys,
} from '../../../../api/users-api';
import { useMemo, useState } from 'react';
import AddSecondaryDomainsDialog from './add-secondary-domains-dialog';
import DeleteSecondaryDomainsDialog from './delete-secondary-domains-dialog';
import SecondaryDomainsToolbar from './secondary-domains-toolbar';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import StatusLabel, { StatusType } from '../../../../shared/status-label';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import SharedTabHeader from '../../../../shared/shared-tab-header';

export default function SecondaryDomains() {
  const [openAddSecondaryDomain, setOpenAddSecondaryDomain] = useState(false);
  const [openDeleteSecondaryDomain, setOpenDeleteSecondaryDomain] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { administrationRoles, primaryDomain } = useUserSettings();

  const {
    data: possibleSecondaryDomains,
    isLoading: isSecondaryDomainsLoading,
    isFetching: isSecondaryDomainsFetching,
  } = useQuery({
    queryKey: [UserKeys.AllPossibleDomains],
    queryFn: () => getAllPossibleSecondaryDomains(),
  });

  const {
    data: addedSecondaryDomains,
    isLoading: isLoading,
    isFetching: isFetching,
  } = useQuery({
    queryKey: [UserKeys.SecondaryDomains],
    queryFn: () => getSecondaryDomains(),
  });

  const filteredSecondaryDomains = addedSecondaryDomains?.filter(
    (domain) => domain.domain !== primaryDomain
  );

  const transformedPossibleSecondaryDomains = useMemo(() => {
    const googleDomains =
      possibleSecondaryDomains?.filter((item) => item !== primaryDomain) || [];
    const gPanelDomains = filteredSecondaryDomains?.map((d) => d.domain) || [];
    const gPanelDomainsSet = new Set(gPanelDomains);
    const allDomains = Array.from(
      new Set([...googleDomains, ...gPanelDomains])
    );

    return allDomains.map((domain) => ({
      domain,
      status: gPanelDomainsSet.has(domain) ? 'Enabled' : 'Disabled',
    }));
  }, [filteredSecondaryDomains, possibleSecondaryDomains, primaryDomain]);

  const selectDomains = transformedPossibleSecondaryDomains?.filter((domain) =>
    selectedRows.includes(domain.domain)
  );

  const containsEnabledDomains = selectDomains.some(
    (device) => device.status === 'Enabled'
  );

  const containsDisabledDomains = selectDomains.some(
    (device) => device.status === 'Disabled'
  );

  const columns: GridColDef[] = [
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 2,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 2,
      renderCell: (params) => {
        const status = (params.value?.toUpperCase() as StatusType) ?? 'ENABLED';
        return <StatusLabel status={status} />;
      },
    },
  ];

  const toolbarProps = useMemo(
    () => ({
      administrationRoles,
      selectedRows,
      containsDisabledDomains,
      containsEnabledDomains,
      setOpenDeleteSecondaryDomain,
      setOpenAddSecondaryDomain,
    }),
    [
      administrationRoles,
      containsDisabledDomains,
      containsEnabledDomains,
      selectedRows,
    ]
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Secondary Domains" />
      <Box sx={{ px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The grid below displays all possible secondary domains attached to
          your current primary domain. You can enable secondary domains to make
          those resources accessible to gPanel.
        </Typography>
        <Stack flexDirection="row" alignItems="baseline" gap={2}>
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            Your Primary Domain:
          </Typography>
          <Chip label={primaryDomain} size="small" color="primary" />
        </Stack>
        <Box sx={{ height: '56vh', mx: 'auto' }}>
          <SharedDataGridClient
            slots={{
              toolbar: useToolbar(SecondaryDomainsToolbar, toolbarProps),
              ...createGridOverlays('Secondary Domains', <AlternateEmail />),
            }}
            slotProps={{
              toolbar: {
                administrationRoles,
                selectedRows,
                containsDisabledDomains,
                containsEnabledDomains,
                setOpenDeleteSecondaryDomain,
                setOpenAddSecondaryDomain,
              } as never,
            }}
            checkboxSelection
            columns={columns}
            rows={transformedPossibleSecondaryDomains || []}
            getRowId={(row) => row.domain}
            loading={
              isSecondaryDomainsLoading ||
              isSecondaryDomainsFetching ||
              isLoading ||
              isFetching
            }
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </Box>
      </Box>
      {openAddSecondaryDomain && selectedRows ? (
        <AddSecondaryDomainsDialog
          open={openAddSecondaryDomain}
          handleClose={() => setOpenAddSecondaryDomain(false)}
          selectedRows={selectDomains}
          setSelectedRows={setSelectedRows}
        />
      ) : null}

      {openDeleteSecondaryDomain && selectedRows ? (
        <DeleteSecondaryDomainsDialog
          open={openDeleteSecondaryDomain}
          handleClose={() => setOpenDeleteSecondaryDomain(false)}
          selectedRows={selectDomains}
          setSelectedRows={setSelectedRows}
        />
      ) : null}
    </Stack>
  );
}
